<template>
  <div class="portal-config-container">
    <el-row :gutter="20" class="portal-config-layout">
      <!-- ⬅️ 左侧：7 大处室分类列表面板 -->
      <el-col :span="10" class="panel-left">
        <el-card class="box-card config-card" shadow="never">
          <div slot="header" class="card-header">
            <span class="card-title">
              <i class="el-icon-folder-opened brand-icon"></i>
              大桌面板块分类配置
            </span>
            <div>
              <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddCategory"
                style="margin-right: 6px;">
                新增配置分类
              </el-button>
              <el-button type="danger" plain size="mini" icon="el-icon-refresh-left" @click="handleResetDefault">
                恢复默认分类
              </el-button>
            </div>
          </div>

          <div class="groups-list">
            <div v-for="(item, index) in groups" :key="item.key" class="group-row-item"
              :class="{ 'is-active': activeGroupKey === item.key }" @click="handleSelectGroup(item.key)">
              <!-- 序号展示 -->
              <div class="row-sort-badge" :title="'序号 ' + (item.orderSort || index + 1)">
                {{ item.orderSort || index + 1 }}
              </div>

              <!-- 默认矢量图标 -->
              <div class="row-icon-wrap" :class="'theme-' + item.key">
                <i :class="item.icon"></i>
              </div>

              <!-- 板块名称 + 挂接数量（紧挨，避免中间留白） -->
              <div class="row-name-block">
                <div class="row-name-text" :title="item.name">{{ item.name }}</div>
                <button
                  type="button"
                  class="row-menu-count"
                  :class="{ 'is-empty': !(item.menus && item.menus.length) }"
                  title="点击调整已挂接一级菜单顺序"
                  @click.stop="openLinkedMenusForGroup(item)"
                >
                  {{ (item.menus && item.menus.length) || 0 }} 个菜单
                  <i class="el-icon-sort"></i>
                </button>
              </div>

              <!-- 显隐启用开关 -->
              <div class="row-status-switch" @click.stop>
                <el-switch v-model="item.visible" active-color="#10b981" inactive-color="#e2e8f0" active-text="启用"
                  inactive-text="隐藏" :active-value="true" :inactive-value="false"></el-switch>
              </div>

              <!-- 详情 / 删除 -->
              <div class="row-actions" @click.stop>
                <el-button type="text" icon="el-icon-edit-outline" title="编辑详情（改序号）"
                  @click="openEditCategory(item)"></el-button>
                <el-button v-if="!isBuiltInGroup(item.key)" type="text" icon="el-icon-delete"
                  style="color: #f87171;" title="删除此分类板块"
                  @click="handleDeleteGroup(item.key)"></el-button>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- ➡️ 右侧：高级手风琴折叠卡片挂接面板 (重构代替长难el-tree) -->
      <el-col :span="14" class="panel-right">
        <el-card class="box-card config-card" shadow="never">
          <div slot="header" class="card-header">
            <span class="card-title">
              <i class="el-icon-menu brand-icon"></i>
              菜单挂接关联池
              <el-tag size="small" type="success" style="margin-left: 10px;">
                当前配置：{{ activeGroupName }}
              </el-tag>
            </span>
            <el-button type="primary" size="mini" icon="el-icon-check" :loading="saving" @click="handleSaveAllConfig">
              保存系统全局版面
            </el-button>
          </div>

          <!-- 搜索过滤条 -->
          <div class="search-bar">
            <el-input size="small" placeholder="输入功能名称快速搜索过滤..." prefix-icon="el-icon-search" v-model="filterText"
              clearable></el-input>
            <div class="search-bar-tip">同一菜单可挂到多个业务域（如「人员管理」）；卡片上会标注已挂接的其他分类</div>
          </div>

          <!-- 升级后的折叠卡片列表 -->
          <div class="menu-collapse-panel">
            <el-collapse v-model="activeCollapseNames" class="custom-el-collapse">
              <el-collapse-item v-for="cat in filteredCollapseData" :key="cat.label" :name="cat.label">
                <!-- 折叠板标题 -->
                <template slot="title">
                  <div class="collapse-header-title">
                    <i class="el-icon-folder" style="color: #64748b; margin-right: 6px;"></i>
                    <span class="title-text">{{ cat.label }}</span>
                    <el-tag size="mini" type="info" class="count-tag">{{ cat.list.length }} 个应用</el-tag>
                  </div>
                </template>

                <!-- 应用卡片网格 -->
                <div class="app-card-grid">
                  <div v-for="app in cat.list" :key="app.id" class="app-check-card"
                    :class="{
                      'is-checked': isAppChecked(app.id),
                      'is-shared': getOtherLinkedNames(app.id).length > 0
                    }"
                    @click="handleToggleApp(app.id)">
                    <!-- 选择框与卡片图标 -->
                    <div class="app-card-left">
                      <el-checkbox v-model="checkedAppMap[app.id]" @change="handleCheckboxChange($event, app.id)"
                        @click.native.stop></el-checkbox>
                      <span class="app-card-icon-wrap">
                        <i :class="resolveMenuIconClass(app.icon)" class="app-card-icon"></i>
                      </span>
                      <span class="app-card-name-wrap">
                        <span class="app-card-name" :title="app.name">{{ app.name }}</span>
                        <el-tooltip
                          v-if="getOtherLinkedNames(app.id).length"
                          effect="dark"
                          placement="top"
                          :content="'已挂：' + getOtherLinkedNames(app.id).join('、')"
                        >
                          <span class="app-card-hint is-shared-hint">
                            已挂 {{ getOtherLinkedNames(app.id).length }} 处：{{ getOtherLinkedNames(app.id).join('、') }}
                          </span>
                        </el-tooltip>
                        <span v-else class="app-card-hint">门户卡片入口</span>
                      </span>
                    </div>
                  </div>
                </div>
              </el-collapse-item>
            </el-collapse>

            <el-empty v-if="filteredCollapseData.length === 0" description="没有找到匹配的功能卡片"></el-empty>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- ⚙️ 新增 / 编辑分类板块弹窗（可改序号） -->
    <el-dialog
      :title="categoryEditKey ? '编辑大桌面分类板块' : '新增大桌面分类板块'"
      :visible.sync="categoryDialogVisible"
      width="560px"
      append-to-body
      custom-class="portal-category-dialog"
    >
      <el-form :model="categoryForm" label-width="88px" ref="categoryFormRef" size="small">
        <el-form-item label="板块名称" required>
          <el-input v-model="categoryForm.name" placeholder="请输入分类板块名称，如：体卫艺处"></el-input>
        </el-form-item>
        <el-form-item label="板块图标" required>
          <div class="icon-board">
            <button
              v-for="item in iconOptions"
              :key="item.value"
              type="button"
              class="icon-board-cell"
              :class="{ 'is-active': categoryForm.icon === item.value }"
              :title="item.label"
              @click="categoryForm.icon = item.value"
            >
              <i :class="item.value"></i>
            </button>
          </div>
          <div class="form-tip">已选：{{ selectedIconLabel }}</div>
        </el-form-item>
        <el-form-item label="排序序号" required>
          <el-input-number
            v-model="categoryForm.orderSort"
            :min="1"
            :max="Math.max(groups.length + 1, 99)"
            :step="1"
            controls-position="right"
            style="width: 160px;"
          ></el-input-number>
          <div class="form-tip">数字越小越靠前；保存后列表会按序号自动重排</div>
        </el-form-item>
        <el-form-item v-if="categoryEditKey" label="是否启用">
          <el-switch
            v-model="categoryForm.visible"
            active-color="#10b981"
            inactive-color="#e2e8f0"
            active-text="启用"
            inactive-text="隐藏"
          ></el-switch>
        </el-form-item>
        <el-form-item v-if="categoryEditKey" label="已挂接">
          <el-button type="text" class="linked-menus-btn" @click="openLinkedMenusDialog">
            {{ categoryForm.menuCount }} 个一级模块
            <i class="el-icon-arrow-right"></i>
          </el-button>
          <div class="form-tip">点击可查看并调整门户侧栏中的一级菜单顺序；经典布局仍按菜单原序号</div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="categoryDialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" @click="saveCategoryDialog">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 已挂接一级菜单：门户自定义序号 -->
    <el-dialog
      title="已挂接一级菜单排序"
      :visible.sync="linkedMenusDialogVisible"
      width="520px"
      append-to-body
    >
      <div class="linked-tip">
        门户布局按下方序号展示；经典布局忽略此处，使用菜单权限里的原序号。
      </div>
      <div v-if="linkedMenuRows.length" class="linked-menus-list">
        <div v-for="(row, idx) in linkedMenuRows" :key="row.id" class="linked-menu-row">
          <el-input-number
            v-model="row.orderSort"
            :min="1"
            :max="linkedMenuRows.length"
            size="mini"
            controls-position="right"
            class="linked-sort-input"
          ></el-input-number>
          <span class="linked-menu-icon"><i :class="resolveMenuIconClass(row.icon)"></i></span>
          <span class="linked-menu-name" :title="row.name">{{ row.name }}</span>
          <span class="linked-menu-idx">#{{ idx + 1 }}</span>
        </div>
      </div>
      <el-empty v-else description="当前分类尚未挂接一级菜单" :image-size="72"></el-empty>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="linkedMenusDialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :disabled="!linkedMenuRows.length" @click="saveLinkedMenusOrder">保存顺序</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getPermissionTree } from '../../api/modules/system';
import { saveGlobalConfig, getGlobalConfig } from '../../api/modules/misc';
import {
  UNCLASSIFIED_GROUP_TPL,
  buildDefaultGroupsFromMenus
} from '../../constants/portalDeptDefaults';

// 将菜单 ID 统一为字符串，避免 long/雪花 ID 经 NumberConverter 序列化为字符串后，
// 再被 parseInt 精度丢失或与 number 严格比对导致挂接失效
const normalizeMenuId = (id) => (id === null || id === undefined || id === '' ? '' : String(id));
const normalizeMenuIds = (ids) => {
  if (!Array.isArray(ids)) return [];
  const seen = new Set();
  const result = [];
  ids.forEach(id => {
    const sid = normalizeMenuId(id);
    if (!sid || seen.has(sid)) return;
    seen.add(sid);
    result.push(sid);
  });
  return result;
};
const menuIdsInclude = (ids, id) => {
  const target = normalizeMenuId(id);
  if (!target || !Array.isArray(ids)) return false;
  return ids.some(x => normalizeMenuId(x) === target);
};

export default {
  name: 'PortalLayoutConfig',
  data() {
    return {
      groups: [],                 // 当前正在编辑的 7 大处室配置列表
      activeGroupKey: 'info',    // 当前激活/选中的处室 key
      menuTreeRaw: [],           // 后台原始菜单树
      collapseData: [],          // 解析打平后的两级目录卡片数据
      activeCollapseNames: [],   // 展开的手风琴面板名数组
      checkedAppMap: {},         // 当前激活处室的菜单 ID 选中哈希映射 { appID: boolean }
      filterText: '',            // 搜索过滤词
      saving: false,             // 保存 Loading 状态
      categoryDialogVisible: false, // 是否显示新增/编辑分类弹窗
      categoryEditKey: '', // 编辑中的分类 key；空表示新增
      categoryForm: { name: '', icon: 'el-icon-folder', orderSort: 1, visible: true, menuCount: 0 },
      linkedMenusDialogVisible: false, // 已挂接一级菜单排序弹窗
      linkedMenuRows: [], // { id, name, icon, orderSort }
      iconOptions: [ // 看板式图标池（Element UI，按 value 去重）
        { label: '显示器', value: 'el-icon-monitor' },
        { label: '处理器', value: 'el-icon-cpu' },
        { label: '笔记本', value: 'el-icon-notebook-1' },
        { label: '笔记本2', value: 'el-icon-notebook-2' },
        { label: '安全锁', value: 'el-icon-lock' },
        { label: '开锁', value: 'el-icon-unlock' },
        { label: '奖章', value: 'el-icon-medal' },
        { label: '奖杯', value: 'el-icon-trophy' },
        { label: '食品', value: 'el-icon-food' },
        { label: '趋势图', value: 'el-icon-data-analysis' },
        { label: '折线', value: 'el-icon-data-line' },
        { label: '柱状图', value: 'el-icon-s-data' },
        { label: '文件夹', value: 'el-icon-folder' },
        { label: '打开文件夹', value: 'el-icon-folder-opened' },
        { label: '设置', value: 'el-icon-setting' },
        { label: '工具', value: 'el-icon-s-tools' },
        { label: '铃铛', value: 'el-icon-bell' },
        { label: '消息', value: 'el-icon-message' },
        { label: '消息实心', value: 'el-icon-message-solid' },
        { label: '钥匙', value: 'el-icon-key' },
        { label: '用户', value: 'el-icon-user' },
        { label: '用户实心', value: 'el-icon-user-solid' },
        { label: '团队', value: 'el-icon-s-custom' },
        { label: '家', value: 'el-icon-s-home' },
        { label: '办公楼', value: 'el-icon-office-building' },
        { label: '地点', value: 'el-icon-location' },
        { label: '地图', value: 'el-icon-map-location' },
        { label: '相机', value: 'el-icon-camera' },
        { label: '摄像头', value: 'el-icon-video-camera' },
        { label: '手机', value: 'el-icon-mobile-phone' },
        { label: '电话', value: 'el-icon-phone' },
        { label: '电话线', value: 'el-icon-phone-outline' },
        { label: '打印机', value: 'el-icon-printer' },
        { label: '文档', value: 'el-icon-document' },
        { label: '复制', value: 'el-icon-document-copy' },
        { label: '已审文档', value: 'el-icon-document-checked' },
        { label: '编辑', value: 'el-icon-edit' },
        { label: '日期', value: 'el-icon-date' },
        { label: '时间', value: 'el-icon-time' },
        { label: '闹钟', value: 'el-icon-alarm-clock' },
        { label: '购物车', value: 'el-icon-shopping-cart-2' },
        { label: '钱包', value: 'el-icon-wallet' },
        { label: '银行卡', value: 'el-icon-bank-card' },
        { label: '货物', value: 'el-icon-box' },
        { label: '卡车', value: 'el-icon-truck' },
        { label: '旗舰', value: 'el-icon-s-flag' },
        { label: '星星', value: 'el-icon-star-on' },
        { label: '空星', value: 'el-icon-star-off' },
        { label: '收藏夹', value: 'el-icon-collection' },
        { label: '菜单', value: 'el-icon-menu' },
        { label: '网格', value: 'el-icon-s-grid' },
        { label: '连接', value: 'el-icon-link' },
        { label: '分享', value: 'el-icon-share' },
        { label: '上传', value: 'el-icon-upload' },
        { label: '下载', value: 'el-icon-download' },
        { label: '刷新', value: 'el-icon-refresh' },
        { label: '搜索', value: 'el-icon-search' },
        { label: '视图', value: 'el-icon-view' },
        { label: '警告', value: 'el-icon-warning-outline' },
        { label: '成功', value: 'el-icon-success' },
        { label: '信息', value: 'el-icon-info' },
        { label: '帮助', value: 'el-icon-question' },
        { label: '图片', value: 'el-icon-picture' },
        { label: '图片框', value: 'el-icon-picture-outline' },
        { label: '影片', value: 'el-icon-film' },
        { label: '麦克风', value: 'el-icon-microphone' },
        { label: '耳机', value: 'el-icon-headset' },
        { label: '阳光', value: 'el-icon-sunny' },
        { label: '月亮', value: 'el-icon-moon' },
        { label: '热水', value: 'el-icon-hot-water' },
        { label: '大雨', value: 'el-icon-heavy-rain' },
        { label: '坐标', value: 'el-icon-place' },
        { label: '指南', value: 'el-icon-guide' },
        { label: '阅读', value: 'el-icon-reading' },
        { label: '公告', value: 'el-icon-s-opportunity' },
        { label: '推广', value: 'el-icon-s-promotion' },
        { label: '聊天', value: 'el-icon-chat-dot-round' },
        { label: '评论', value: 'el-icon-chat-line-square' },
        { label: '服务', value: 'el-icon-service' },
        { label: '运维', value: 'el-icon-set-up' },
        { label: '连接器', value: 'el-icon-connection' },
        { label: '电源', value: 'el-icon-switch-button' },
        { label: '水杯', value: 'el-icon-water-cup' },
        { label: '咖啡杯', value: 'el-icon-coffee-cup' },
        { label: '礼物', value: 'el-icon-present' },
        { label: '标签', value: 'el-icon-price-tag' },
        { label: '折扣', value: 'el-icon-discount' },
        { label: '票据', value: 'el-icon-tickets' },
        { label: '定位', value: 'el-icon-position' },
        { label: '目标', value: 'el-icon-aim' },
        { label: '发现', value: 'el-icon-discover' },
        { label: '魔术棒', value: 'el-icon-magic-stick' },
        { label: '刷子', value: 'el-icon-brush' },
        { label: '完成', value: 'el-icon-finished' },
        { label: '订单', value: 'el-icon-s-order' },
        { label: '营销', value: 'el-icon-s-marketing' },
        { label: '协作', value: 'el-icon-s-cooperation' },
        { label: '平台', value: 'el-icon-s-platform' },
        { label: '店铺', value: 'el-icon-s-shop' },
        { label: '财务', value: 'el-icon-s-finance' },
        { label: '索赔', value: 'el-icon-s-claim' },
        { label: '开放', value: 'el-icon-s-open' },
        { label: '管理', value: 'el-icon-s-management' },
        { label: '评论区', value: 'el-icon-s-comment' },
        { label: '检查', value: 'el-icon-s-check' }
      ]
    };
  },
  computed: {
    selectedIconLabel() {
      const hit = (this.iconOptions || []).find(x => x.value === this.categoryForm.icon);
      return hit ? `${hit.label}（${hit.value}）` : (this.categoryForm.icon || '未选择');
    },
    activeGroupName() {
      const g = this.groups.find(x => x.key === this.activeGroupKey);
      return g ? g.name : '';
    },
    /**
     * 菜单 ID -> 已挂接的其他业务域名列表（不含当前选中分类）
     * 支持同一菜单多业务域共享挂接
     */
    otherLinkedNameMap() {
      const map = {};
      const active = this.activeGroupKey;
      (this.groups || []).forEach(g => {
        if (!g || g.key === 'unclassified' || g.name === '未分类') return;
        if (g.key === active) return;
        normalizeMenuIds(g.menus).forEach(id => {
          if (!map[id]) map[id] = [];
          map[id].push(g.name || g.key);
        });
      });
      return map;
    },
    // 依据搜索关键字，对折叠面板中的卡片进行实时过滤
    filteredCollapseData() {
      if (!this.filterText) return this.collapseData;

      const val = this.filterText.trim().toLowerCase();
      const result = [];

      this.collapseData.forEach(cat => {
        const matchedList = cat.list.filter(app =>
          app.name.toLowerCase().includes(val)
        );
        if (matchedList.length > 0) {
          result.push({
            label: cat.label,
            list: matchedList
          });
        }
      });
      return result;
    }
  },
  methods: {
    // 判定当前分类是否为系统默认内置板块 (内置板块不允许删除)
    isBuiltInGroup(key) {
      return ['unclassified', 'info', 'equipment', 'teaching', 'security', 'moral', 'general', 'principal'].includes(key);
    },
    /**
     * 解析菜单图标 class：兼容 fa-xxx / fa fa-xxx / el-icon-xxx；空则文件夹兜底
     */
    resolveMenuIconClass(icon) {
      const raw = (icon || '').toString().trim();
      if (!raw) return 'el-icon-folder';
      if (raw.indexOf('el-icon-') === 0) return raw;
      // 已带 fa 前缀：fa-users 或 fa fa-gift
      if (raw === 'fa' || raw.indexOf('fa ') === 0 || raw.indexOf('fa-') === 0) {
        return raw.indexOf('fa-') === 0 && raw.indexOf(' ') < 0 ? ['fa', raw] : raw;
      }
      // 裸名兜底当 font-awesome
      return ['fa', 'fa-' + raw];
    },
    // 规范化某一处室配置中的 menus 字段
    normalizeGroupMenus(group) {
      if (!group) return group;
      group.menus = normalizeMenuIds(group.menus);
      return group;
    },
    // 优先选中有内容的「未分类」，否则选中第一个正式板块
    selectPrimaryGroup() {
      const pending = this.groups.find(g => g.key === 'unclassified' && (g.menus || []).length > 0);
      if (pending) {
        this.handleSelectGroup('unclassified');
        return;
      }
      const first = this.groups.find(g => g.key !== 'unclassified') || this.groups[0];
      if (first) {
        this.handleSelectGroup(first.key);
      }
    },
    // 将未挂接菜单归入「未分类」；无漏挂时不展示空的「未分类」
    collectUnassignedMenus(loadedGroups) {
      const flatAllIds = [];
      this.collapseData.forEach(cat => {
        cat.list.forEach(app => {
          flatAllIds.push(normalizeMenuId(app.id));
        });
      });

      const assignedIds = new Set();
      loadedGroups.forEach(g => {
        if (g.key === 'unclassified' || g.name === '未分类') return;
        normalizeMenuIds(g.menus).forEach(id => assignedIds.add(id));
      });

      const unassignedIds = flatAllIds.filter(id => id && !assignedIds.has(id));

      // 先清掉旧的未分类，再按需重建
      for (let i = loadedGroups.length - 1; i >= 0; i--) {
        const g = loadedGroups[i];
        if (g.key === 'unclassified' || g.name === '未分类') {
          loadedGroups.splice(i, 1);
        }
      }

      if (unassignedIds.length > 0) {
        loadedGroups.unshift({
          ...UNCLASSIFIED_GROUP_TPL,
          menus: normalizeMenuIds(unassignedIds),
          visible: true
        });
        console.log(`[PortalLayoutConfig] 自动将未分配的 ${unassignedIds.length} 个菜单预挂接至「未分类」`);
        this.$message.info({
          message: `系统自动检测到有 ${unassignedIds.length} 个菜单未挂载，已放入「未分类」，请在右侧调整挂接后点击「保存系统全局版面」！`,
          duration: 6000,
          showClose: true
        });
      }
      return loadedGroups;
    },
    // 保存成功后同步到 Vuex / localStorage，保证门户首页即时生效
    syncConfigToClient(groups) {
      const payload = (groups || []).map(g => ({
        ...g,
        menus: normalizeMenuIds(g.menus)
      }));
      this.$store.commit('savePortalDeptGroups', payload);
      window.localStorage.setItem('global_dept_groups', JSON.stringify(payload));
    },
    // 从左侧列表直接打开某分类的一级菜单排序
    openLinkedMenusForGroup(item) {
      if (!item || !item.key) return;
      this.categoryEditKey = item.key;
      this.handleSelectGroup(item.key);
      this.openLinkedMenusDialog();
    },
    // 打开已挂接一级菜单排序弹窗
    openLinkedMenusDialog() {
      const key = this.categoryEditKey;
      if (!key) return;
      const group = this.groups.find(g => g.key === key);
      if (!group) {
        this.$message.warning('未找到当前分类');
        return;
      }

      // 菜单字典：从挂接池扁平数据取名称/图标
      const menuMap = {};
      (this.collapseData || []).forEach(cat => {
        (cat.list || []).forEach(app => {
          const sid = normalizeMenuId(app.id);
          if (sid) menuMap[sid] = app;
        });
      });

      const ids = normalizeMenuIds(group.menus || []);
      if (!ids.length) {
        this.$message.info('该分类暂无挂接菜单，请先在右侧勾选后再排序');
        return;
      }

      this.linkedMenuRows = ids.map((id, idx) => {
        const app = menuMap[id] || {};
        return {
          id,
          name: app.name || ('菜单 ' + id),
          icon: app.icon || 'el-icon-folder',
          orderSort: idx + 1
        };
      });
      this.linkedMenusDialogVisible = true;
    },
    // 保存一级菜单门户自定义顺序（写入 menus 数组顺序）
    saveLinkedMenusOrder() {
      const key = this.categoryEditKey;
      const group = this.groups.find(g => g.key === key);
      if (!group) return;

      const rows = (this.linkedMenuRows || []).slice().sort((a, b) => {
        return (Number(a.orderSort) || 0) - (Number(b.orderSort) || 0);
      });
      rows.forEach((row, idx) => {
        row.orderSort = idx + 1;
      });
      group.menus = normalizeMenuIds(rows.map(r => r.id));
      this.linkedMenuRows = rows;
      this.categoryForm.menuCount = group.menus.length;
      this.linkedMenusDialogVisible = false;

      // 若当前正在编辑该分类的右侧勾选态，保持勾选映射与顺序一致
      if (this.activeGroupKey === key) {
        const nextMap = {};
        group.menus.forEach(id => {
          nextMap[id] = true;
        });
        // 保留未勾选项的 false 状态不需要；切换分组时会重建
        this.checkedAppMap = { ...this.checkedAppMap };
        Object.keys(this.checkedAppMap).forEach(id => {
          this.checkedAppMap[id] = false;
        });
        group.menus.forEach(id => {
          this.$set(this.checkedAppMap, id, true);
        });
      }

      this.$message.success('一级菜单顺序已更新。请点击右上角「保存系统全局版面」同步云端！');
    },
    // 打开新增大类弹窗
    openAddCategory() {
      const maxSort = this.groups.length > 0 ? Math.max(...this.groups.map(g => Number(g.orderSort) || 0)) : 0;
      this.categoryEditKey = '';
      this.categoryForm = {
        name: '',
        icon: 'el-icon-folder',
        orderSort: maxSort + 1,
        visible: true,
        menuCount: 0
      };
      this.categoryDialogVisible = true;
    },
    // 打开编辑详情弹窗（可改序号、名称、图标、启用）
    openEditCategory(item) {
      if (!item) return;
      this.categoryEditKey = item.key;
      this.categoryForm = {
        name: item.name || '',
        icon: item.icon || 'el-icon-folder',
        orderSort: Number(item.orderSort) || 1,
        visible: item.visible !== false,
        menuCount: Array.isArray(item.menus) ? item.menus.length : 0
      };
      this.categoryDialogVisible = true;
    },
    // 列表内上移/下移，立即重排序号
    moveGroup(index, delta) {
      const target = index + delta;
      if (index < 0 || target < 0 || index >= this.groups.length || target >= this.groups.length) return;
      const list = this.groups.slice();
      const tmp = list[index];
      list[index] = list[target];
      list[target] = tmp;
      list.forEach((g, idx) => {
        g.orderSort = idx + 1;
      });
      this.groups = list;
      this.$message.success('顺序已调整，请点击右上角「保存系统全局版面」同步云端');
    },
    // 按 orderSort 排序并规范为连续 1..n
    resortGroupsByOrderSort() {
      const list = this.groups.slice().sort((a, b) => {
        const valA = Number(a.orderSort) || 0;
        const valB = Number(b.orderSort) || 0;
        return valA - valB;
      });
      list.forEach((g, idx) => {
        g.orderSort = idx + 1;
      });
      this.groups = list;
    },
    // 弹窗确定：新增或保存编辑
    saveCategoryDialog() {
      if (!this.categoryForm.name.trim()) {
        this.$message.warning('请输入分类板块名称');
        return;
      }
      const sortVal = Number(this.categoryForm.orderSort);
      if (!sortVal || sortVal < 1) {
        this.$message.warning('请填写有效的排序序号（从 1 开始）');
        return;
      }

      if (this.categoryEditKey) {
        const target = this.groups.find(g => g.key === this.categoryEditKey);
        if (!target) {
          this.$message.error('未找到要编辑的分类');
          return;
        }
        target.name = this.categoryForm.name.trim();
        target.icon = this.categoryForm.icon || 'el-icon-folder';
        target.orderSort = sortVal;
        target.visible = this.categoryForm.visible !== false;
        this.resortGroupsByOrderSort();
        this.categoryDialogVisible = false;
        this.$message.success('分类已更新并按序号重排。请点击右上角「保存系统全局版面」同步云端！');
        return;
      }

      const newCategory = {
        key: 'custom_' + Date.now().toString(),
        name: this.categoryForm.name.trim(),
        icon: this.categoryForm.icon || 'el-icon-folder',
        orderSort: sortVal,
        visible: true,
        menus: []
      };
      this.groups.push(newCategory);
      this.resortGroupsByOrderSort();
      this.categoryDialogVisible = false;
      this.$message.success('新增板块分类成功。请在右侧勾选菜单，并点击右上角「保存系统全局版面」生效！');
      this.handleSelectGroup(newCategory.key);
    },
    // 物理保存新增分类（兼容旧调用名）
    saveAddCategory() {
      this.saveCategoryDialog();
    },
    // 删除自定义新增的板块分类
    handleDeleteGroup(key) {
      this.$confirm('确定要删除此自定义板块分类吗？删除后该板块下的所有菜单挂接关系将被彻底清除。', '安全警告', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.groups = this.groups.filter(g => g.key !== key);
        this.$message.success('分类已在内存中移除。请点击右上角「保存系统全局版面」以将改动同步至云端！');

        // 如果删除了当前激活选中项，则重定向选中第一个可用板块
        if (this.activeGroupKey === key && this.groups.length > 0) {
          this.handleSelectGroup(this.groups[0].key);
        }
      }).catch(() => { });
    },
    // 将菜单树转换为与大桌面一级卡片对应的一级分类大类卡片结构，避免碎叶子卡片过于凌乱
    transformTreeToCollapseData(nodes) {
      const result = [
        { label: '系统所有功能大类', list: [] }
      ];

      nodes.forEach(node => {
        // 首页由于是桌面容器本身，不参与桌面内子版块的分配
        if (node.label === '首页' || node.value === 1) {
          return;
        }

        result[0].list.push({
          id: normalizeMenuId(node.value),
          name: node.label,
          // 优先用权限树带回的 Icon（fa-*）；无则文件夹兜底
          icon: (node.iconCls || node.Icon || '').toString().trim() || 'el-icon-folder'
        });
      });

      return result;
    },
    // 将静态出厂名称列表动态转换为当前系统树中的 ID 列表（精确匹配，一菜单只归一处）
    getDefaultConfigWithIds() {
      const flatAll = [];
      this.collapseData.forEach(cat => {
        cat.list.forEach(app => {
          flatAll.push({ id: normalizeMenuId(app.id), name: app.name });
        });
      });
      return buildDefaultGroupsFromMenus(flatAll).map(g => this.normalizeGroupMenus(g));
    },
    // 按出厂默认重建：规则表命中的进对应处室，其余一级菜单一律进信息中心（门户全覆盖）
    rebuildToDefaultGroups() {
      // 全量默认分配（含规则外 → info），不含「未分类」
      let nextGroups = this.getDefaultConfigWithIds();
      nextGroups = nextGroups.filter(g => g.key !== 'unclassified' && !String(g.key || '').startsWith('custom_'));
      // 仍保留空的「未分类」板块，方便后续新增菜单时定位
      nextGroups = this.collectUnassignedMenus(nextGroups);
      nextGroups.sort((a, b) => (a.orderSort || 0) - (b.orderSort || 0));
      nextGroups.forEach((g, idx) => {
        g.orderSort = idx + 1;
        this.normalizeGroupMenus(g);
      });
      return nextGroups;
    },
    // 初始化配置：优先拉取云端全局配置，没有则使用出厂兜底
    initPortalConfig() {
      // 1. 先拉取系统全量菜单树
      getPermissionTree({ needbtn: false }).then(res => {
        if (res && res.data && res.data.response) {
          this.menuTreeRaw = res.data.response.children || [];
          // 动态打平并分组菜单
          this.collapseData = this.transformTreeToCollapseData(this.menuTreeRaw);
          // 默认全部展开
          this.activeCollapseNames = this.collapseData.map(x => x.label);

          // 2. 菜单就绪后，再拉取云端大桌面处室配置（全局系统配置，与 userId 无关）
          getGlobalConfig({
            key: 'dept_groups'
          }).then(gRes => {
            let loadedGroups = [];
            if (gRes.data.success && gRes.data.response) {
              const list = JSON.parse(gRes.data.response);
              list.forEach(item => {
                if (item.name) {
                  item.name = item.name.replace(/^[💻🏫👨‍🏫👮🏆🍕📊]\s*/g, '');
                }
                item.menus = normalizeMenuIds(item.menus);
              });
              loadedGroups = list.sort((a, b) => (a.orderSort || 0) - (b.orderSort || 0));
            } else {
              loadedGroups = this.getDefaultConfigWithIds();
            }

            // 【增量自动补齐】未挂接菜单归入「未分类」，便于快速定位
            loadedGroups = this.collectUnassignedMenus(loadedGroups);

            this.groups = loadedGroups;

            // 有漏挂才聚焦「未分类」，否则聚焦第一个正式板块（避免空「未分类」造成未配置错觉）
            this.selectPrimaryGroup();
          }).catch(() => {
            let fallback = this.getDefaultConfigWithIds();
            fallback = this.collectUnassignedMenus(fallback);
            this.groups = fallback;
            this.selectPrimaryGroup();
          });
        }
      }).catch(err => {
        this.$message.error('获取系统菜单树失败，请确认系统 API 联通状态');
        console.error(err);
      });
    },
    // 切换选中左侧的处室，联动右侧多选框状态的渲染
    handleSelectGroup(key) {
      this.activeGroupKey = key;
      const currentGroup = this.groups.find(x => x.key === key);
      const menus = currentGroup ? normalizeMenuIds(currentGroup.menus) : [];

      // 生成新的选中状态哈希映射表（键统一用字符串 ID）
      const newMap = {};
      this.collapseData.forEach(cat => {
        cat.list.forEach(app => {
          const sid = normalizeMenuId(app.id);
          newMap[sid] = menuIdsInclude(menus, sid);
        });
      });
      this.checkedAppMap = newMap;
    },
    // 判断卡片是否处于勾选状态
    isAppChecked(id) {
      return !!this.checkedAppMap[normalizeMenuId(id)];
    },
    /**
     * 查询菜单已挂接到的「其他」业务域名称（不含当前选中分类）
     */
    getOtherLinkedNames(menuId) {
      const sid = normalizeMenuId(menuId);
      if (!sid) return [];
      return this.otherLinkedNameMap[sid] || [];
    },
    // 点击应用卡片整行时，切换选中状态（仅影响当前分类，不踢掉其他分类的挂接）
    handleToggleApp(id) {
      const sid = normalizeMenuId(id);
      const nextVal = !this.checkedAppMap[sid];
      this.$set(this.checkedAppMap, sid, nextVal);
      this.syncActiveGroupMenus();
    },
    // 点击多选框本身时的回调
    handleCheckboxChange(val, id) {
      this.$set(this.checkedAppMap, normalizeMenuId(id), val);
      this.syncActiveGroupMenus();
    },
    // 根据其它处室挂接结果，实时重算「未分类」菜单池
    // 未分类 = 尚未挂到任何一个正式业务域；允许同一菜单同时存在于多个业务域
    refreshUnclassifiedMenus() {
      const assignedIds = new Set();
      this.groups.forEach(g => {
        if (g.key === 'unclassified' || g.name === '未分类') return;
        normalizeMenuIds(g.menus).forEach(id => assignedIds.add(id));
      });

      const flatAllIds = [];
      this.collapseData.forEach(cat => {
        cat.list.forEach(app => {
          flatAllIds.push(normalizeMenuId(app.id));
        });
      });
      const unassignedIds = flatAllIds.filter(id => id && !assignedIds.has(id));

      const idx = this.groups.findIndex(g => g.key === 'unclassified' || g.name === '未分类');
      if (unassignedIds.length === 0) {
        if (idx >= 0) {
          this.groups.splice(idx, 1);
        }
        if (this.activeGroupKey === 'unclassified') {
          this.selectPrimaryGroup();
        }
        return;
      }

      if (idx >= 0) {
        this.groups[idx].menus = normalizeMenuIds(unassignedIds);
        this.groups[idx].visible = true;
      } else {
        this.groups.unshift({
          ...UNCLASSIFIED_GROUP_TPL,
          menus: normalizeMenuIds(unassignedIds),
          visible: true
        });
      }

      if (this.activeGroupKey === 'unclassified') {
        this.handleSelectGroup('unclassified');
      }
    },
    // 同步哈希映射表中的选中项到当前 groups 中该处室的 menus 数组里
    // 保留已有顺序：仍勾选的保持原序，新勾选的追加到末尾（门户自定义序）
    syncActiveGroupMenus() {
      const currentGroup = this.groups.find(x => x.key === this.activeGroupKey);
      if (currentGroup) {
        const prevIds = normalizeMenuIds(currentGroup.menus || []);
        const checkedSet = new Set();
        Object.keys(this.checkedAppMap).forEach(id => {
          if (this.checkedAppMap[id]) {
            checkedSet.add(normalizeMenuId(id));
          }
        });

        const kept = prevIds.filter(id => checkedSet.has(id));
        const keptSet = new Set(kept);
        const appended = [];
        checkedSet.forEach(id => {
          if (id && !keptSet.has(id)) appended.push(id);
        });

        currentGroup.menus = normalizeMenuIds([...kept, ...appended]);

        // 勾选变更后实时刷新未分类池，便于快速定位漏挂菜单
        if (currentGroup.key !== 'unclassified') {
          this.refreshUnclassifiedMenus();
        }
      }
    },
    // 恢复为系统出厂默认配置并直接同步至数据库
    handleResetDefault() {
      this.$confirm('确认要重新按出厂默认规则分类吗？将清空当前处室挂接与自定义分类，按默认表重建后立即保存生效。', '恢复默认警告', {
        confirmButtonText: '确定恢复',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.groups = this.rebuildToDefaultGroups();
        this.selectPrimaryGroup();

        this.saving = true;
        saveGlobalConfig({
          configKey: 'dept_groups',
          configValue: JSON.stringify(this.groups)
        }).then(res => {
          this.saving = false;
          if (res && res.data && res.data.success) {
            this.syncConfigToClient(this.groups);
            const unclassifiedCount = ((this.groups.find(g => g.key === 'unclassified') || {}).menus || []).length;
            const tip = unclassifiedCount > 0
              ? `已按默认规则重新分类并保存！仍有 ${unclassifiedCount} 个菜单在「未分类」，请继续归类后保存。`
              : '已按默认规则重新分类并保存同步至数据库！';
            this.$message.success(tip);
          } else {
            this.$message.error((res && res.data && res.data.msg) || '恢复默认配置保存失败');
          }
        }).catch(err => {
          this.saving = false;
          this.$message.error('请求保存失败，请检查网络联通状态');
          console.error(err);
        });
      }).catch(() => { });
    },
    // 物理保存全部配置至云端
    handleSaveAllConfig() {
      this.saving = true;
      // 保存前先把当前勾选写回激活分组，避免漏同步
      this.syncActiveGroupMenus();

      // 1. 保存前，按照序号从小到大重排
      this.groups.sort((a, b) => {
        const valA = parseInt(a.orderSort) || 0;
        const valB = parseInt(b.orderSort) || 0;
        return valA - valB;
      });

      // 2. 校正排序数字，并统一 menus 为字符串 ID
      this.groups.forEach((g, idx) => {
        g.orderSort = idx + 1;
        this.normalizeGroupMenus(g);
      });

      // 3. 发送云端保存请求（全局系统配置，与 userId 无关）
      saveGlobalConfig({
        configKey: 'dept_groups',
        configValue: JSON.stringify(this.groups)
      }).then(res => {
        this.saving = false;
        if (res && res.data && res.data.success) {
          this.syncConfigToClient(this.groups);
          this.$message.success('系统全局大桌面处室板块分类及菜单挂接配置已成功保存！');
          this.handleSelectGroup(this.activeGroupKey);
        } else {
          this.$message.error((res && res.data && res.data.msg) || '保存云端配置失败');
        }
      }).catch(err => {
        this.saving = false;
        this.$message.error('请求接口失败，请检查网络联通状态');
        console.error(err);
      });
    }
  },
  created() {
    this.initPortalConfig();
  }
};
</script>

<style scoped>
.portal-config-container {
  padding: 15px;
  background-color: #f8fafc;
  min-height: 100%;
  box-sizing: border-box;
}

.portal-config-layout {
  height: 100%;
}

.config-card {
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.015);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.brand-icon {
  font-size: 18px;
  color: #3b82f6;
}

.groups-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.group-row-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.22s ease;
}

.group-row-item:hover {
  border-color: rgba(59, 130, 246, 0.25);
  background-color: #f8fafc;
}

.group-row-item.is-active {
  border-color: #3b82f6;
  background-color: rgba(59, 130, 246, 0.02);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.08);
}

.row-sort-badge {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.group-row-item.is-active .row-sort-badge {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}

.row-name-block {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.row-name-text {
  min-width: 0;
  max-width: 58%;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-row-item.is-active .row-name-text {
  color: #1e3a8a;
}

.row-menu-count {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.row-menu-count i {
  font-size: 12px;
  margin: 0;
}

.row-menu-count:hover {
  background: #dbeafe;
  border-color: #93c5fd;
  color: #1e40af;
}

.row-menu-count.is-empty {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #94a3b8;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-left: 4px;
}

.row-actions .el-button {
  padding: 4px;
  font-size: 15px;
  color: #64748b;
}

.row-actions .el-button:hover {
  color: #2563eb;
}

.row-actions .el-button.is-disabled {
  color: #cbd5e1;
}

.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}

.linked-menus-btn {
  padding: 0 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #2563eb !important;
}

.linked-menus-btn i {
  margin-left: 4px;
}

.linked-tip {
  margin-bottom: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #0369a1;
  font-size: 12px;
  line-height: 1.5;
}

.linked-menus-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
}

.linked-menu-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.linked-sort-input {
  width: 100px;
  flex-shrink: 0;
}

.linked-menu-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f1f5f9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  flex-shrink: 0;
}

.linked-menu-icon i {
  margin: 0 !important;
  font-size: 14px;
}

.linked-menu-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.linked-menu-idx {
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
}

/* 看板式图标选择池 */
.icon-board {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.icon-board-cell {
  width: 100%;
  aspect-ratio: 1 / 1;
  min-height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  padding: 0;
}

.icon-board-cell i {
  font-size: 18px;
  margin: 0 !important;
  line-height: 1;
}

.icon-board-cell:hover {
  border-color: #93c5fd;
  color: #2563eb;
  background: #eff6ff;
}

.icon-board-cell.is-active {
  border-color: #3b82f6;
  color: #1d4ed8;
  background: #dbeafe;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
}

.row-icon-wrap {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

/* 各个处室分类专属莫兰迪背景图标色 */
.row-icon-wrap.theme-unclassified i {
  color: #f97316;
}

.row-icon-wrap.theme-unclassified {
  background-color: rgba(249, 115, 22, 0.1);
}

.row-icon-wrap.theme-info i {
  color: #3b82f6;
}

.row-icon-wrap.theme-info {
  background-color: rgba(59, 130, 246, 0.08);
}

.row-icon-wrap.theme-equipment i {
  color: #8b5cf6;
}

.row-icon-wrap.theme-equipment {
  background-color: rgba(139, 92, 246, 0.08);
}

.row-icon-wrap.theme-teaching i {
  color: #6366f1;
}

.row-icon-wrap.theme-teaching {
  background-color: rgba(99, 102, 241, 0.08);
}

.row-icon-wrap.theme-security i {
  color: #06b6d4;
}

.row-icon-wrap.theme-security {
  background-color: rgba(6, 182, 212, 0.08);
}

.row-icon-wrap.theme-moral i {
  color: #f59e0b;
}

.row-icon-wrap.theme-moral {
  background-color: rgba(245, 158, 11, 0.08);
}

.row-icon-wrap.theme-general i {
  color: #10b981;
}

.row-icon-wrap.theme-general {
  background-color: rgba(16, 185, 129, 0.08);
}

.row-icon-wrap.theme-principal i {
  color: #ec4899;
}

.row-icon-wrap.theme-principal {
  background-color: rgba(236, 72, 153, 0.08);
}

.row-name-input {
  flex: 1;
}

.row-status-switch {
  display: flex;
  align-items: center;
}

.search-bar {
  margin-bottom: 12px;
}

.search-bar-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

/* ================= 升级后的手风琴折叠面板与卡片网格样式 ================= */
.menu-collapse-panel {
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 8px;
  background-color: #f8fafc;
  max-height: 520px;
  overflow-y: auto;
}

.custom-el-collapse {
  border-top: none;
  border-bottom: none;
}

.custom-el-collapse /deep/ .el-collapse-item__header {
  height: 42px;
  line-height: 42px;
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 6px;
  padding: 0 12px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.custom-el-collapse /deep/ .el-collapse-item__header:hover {
  background-color: #f8fafc;
  border-color: #e2e8f0;
}

.custom-el-collapse /deep/ .el-collapse-item__wrap {
  background-color: transparent;
  border-bottom: none;
  padding: 4px 4px 10px 4px;
}

.collapse-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.collapse-header-title .title-text {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.collapse-header-title .count-tag {
  font-weight: normal;
  border-radius: 12px;
  padding: 0 8px;
  transform: scale(0.9);
}

/* 卡片网格：一行排 3 个（响应式分布，大屏 3 列） */
.app-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 6px;
}

/* 多选应用卡片基本态 */
.app-check-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-check-card:hover {
  border-color: rgba(59, 130, 246, 0.35);
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.05);
}

/* 勾选高亮激活态：背景呈极淡淡蓝色，边框发光蔚蓝 */
.app-check-card.is-checked {
  border-color: #3b82f6;
  background-color: rgba(59, 130, 246, 0.03);
  box-shadow: 0 0 0 1px #3b82f6;
}

.app-card-left {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.app-card-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ecf4ff 0%, #e0edff 100%);
  border: 1px solid #d8e5fb;
  flex-shrink: 0;
  overflow: hidden;
}

.app-card-icon {
  font-size: 13px;
  color: #4f77c9;
  flex-shrink: 0;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1 !important;
  width: auto !important;
  height: auto !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.app-check-card.is-checked .app-card-icon {
  color: #ffffff;
}

.app-check-card.is-checked .app-card-icon-wrap {
  background: linear-gradient(135deg, #5a8dff 0%, #3b6fe8 100%);
  border-color: transparent;
}

.app-card-name-wrap {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.app-card-name {
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.app-card-hint {
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.35;
  white-space: normal;
  word-break: break-all;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.app-card-hint.is-shared-hint {
  color: #b45309;
  cursor: help;
}

.app-check-card.is-shared:not(.is-checked) {
  border-color: #fcd34d;
  background: #fffbeb;
}

.app-check-card.is-checked .app-card-name {
  color: #1e3a8a;
  font-weight: 600;
}

.app-check-card.is-checked .app-card-hint {
  color: #5b7eb8;
}

.app-check-card.is-checked .app-card-hint.is-shared-hint {
  color: #c2410c;
}
</style>
